import React, { useState, useEffect, useRef } from 'react';
import ApiClient from "../utils/ApiClient";
import SellerFulfilledOrdersTable from '../components/SFOrders/SellerFulfilledOrdersTable';
import OrderVerificationModal from '../components/SFOrders/OrderVerificationModal';

export default function SellerFulfilledOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingOrders, setProcessingOrders] = useState(new Set());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentRowKey, setCurrentRowKey] = useState(null);
    // Tracks shipped UNITS, keyed by order_location_id so multi-qty orders
    // (one amazon_orders row, N link rows) don't collapse to a single entry.
    const shippedRowsRef = useRef(new Set());

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                //log jwt token
                console.log('jwt token:', localStorage.getItem('jwt'));
                //refresh token
                console.log('refresh token:', localStorage.getItem('refreshToken'));
                const response = await ApiClient.post('/api/orders/getsforders');
                if (response.data && Array.isArray(response.data)) {
                    // Apply shipped status from our ref to the fetched rows
                    const updatedOrders = response.data.map(order => {
                        if (shippedRowsRef.current.has(order.order_location_id)) {
                            return { ...order, sold: 1, order_status: 'Shipped' };
                        }
                        return order;
                    });
                    setOrders(updatedOrders);
                } else {
                    throw new Error('Invalid response structure');
                }
            } catch (error) {
                console.error('Error fetching orders:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, []);

    const handleOpenModal = (rowKey) => {
        setCurrentRowKey(rowKey);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentRowKey(null);
    };

    const handleShipOrder = async (rowKey, modalResult) => {
        if (shippedRowsRef.current.has(rowKey)) {
            return;
        }
        setProcessingOrders(prev => new Set([...prev, rowKey]));
        try {
            shippedRowsRef.current.add(rowKey);
            const newOrders = orders.map(order =>
                order.order_location_id === rowKey
                    ? { ...order, sold: 1, order_status: 'Shipped' }
                    : order
            );
            setOrders(newOrders);
        } catch (error) {
            console.error('Error marking order row as shipped:', error);
        } finally {
            setProcessingOrders(prev => {
                const newSet = new Set(prev);
                newSet.delete(rowKey);
                return newSet;
            });
        }
    };

    // Reflect a just-assigned unit on its order row (FNSKU + location) without a full
    // page reload. Matches on order_location_id, the link-table row key.
    const handleInventoryAssigned = (orderLocationId, data) => {
        const identifier = data?.identifier ?? null;
        setOrders(prev => prev.map(order =>
            order.order_location_id === orderLocationId
                ? {
                    ...order,
                    identifier: identifier ?? order.identifier,
                    fnsku: identifier && /^x/i.test(identifier) ? identifier : order.fnsku,
                    location: data?.location ?? order.location,
                    calibrate_local_inventory_location_id:
                        data?.inventoryId ?? order.calibrate_local_inventory_location_id,
                }
                : order
        ));
    };

    // A unit was marked unable_to_locate with no same-condition replacement — the order
    // line is now unassigned. Clear the unit fields on its row so it reflects reality
    // (and the "Assign Inventory" affordance reappears) without a full reload.
    const handleUnitReleased = (orderLocationId) => {
        setOrders(prev => prev.map(order =>
            order.order_location_id === orderLocationId
                ? {
                    ...order,
                    identifier: null,
                    fnsku: null,
                    location: null,
                    sold: null,
                    sale_pending: null,
                    calibrate_local_inventory_location_id: 0,
                }
                : order
        ));
    };

    const isOrderShipped = (rowKey) => {
        return shippedRowsRef.current.has(rowKey) ||
            orders.some(order => order.order_location_id === rowKey && order.sold === 1);
    };

    if (loading) return <div className="p-4">Loading orders...</div>;
    if (error) return <div className="p-4 text-red-500">Error: {error}</div>;

    const currentOrder = currentRowKey ? orders.find(o => o.order_location_id === currentRowKey) : null;

    return (
        <>
            <SellerFulfilledOrdersTable
                orders={orders}
                processingOrders={processingOrders}
                handleOpenModal={handleOpenModal}
                isOrderShipped={isOrderShipped} // Pass this function to the table
                onInventoryAssigned={handleInventoryAssigned}
                onUnitReleased={handleUnitReleased}
            />
            <OrderVerificationModal
                isOpen={isModalOpen}
                currentOrder={currentOrder}
                onClose={handleCloseModal}
                onOrderShipped={handleShipOrder}
            />
        </>
    );
}